/**
 * Nhost v4 Client
 *
 * Main client setup using @nhost/nhost-js v4.
 * This module provides the singleton Nhost client and server/admin GraphQL helpers.
 *
 * Usage:
 * ```ts
 * import { nhost, unwrapGraphQLResponse } from "@/lib/nhost/client";
 * ```
 */

import type { NhostClient } from "@nhost/nhost-js";
import { createClient, createNhostClient, withAdminSession } from "@nhost/nhost-js";
import type { Session } from "@nhost/nhost-js/session";
import { LocalStorage, MemoryStorage } from "@nhost/nhost-js/session";
import { env, requireServerEnv } from "@/env";

// ============================================================================
// Types
// ============================================================================

/**
 * Hasura JWT claims extracted from the decoded token
 */
export interface HasuraClaims {
  "x-hasura-user-id"?: string;
  "x-hasura-org-id"?: string;
  "x-hasura-org-ids"?: string;
  "x-hasura-org-user-id"?: string;
  "x-hasura-allowed-roles"?: string[];
  "x-hasura-default-role"?: string;
  "x-hasura-user-is-anonymous"?: string;
  [key: string]: unknown;
}

export type { User } from "@nhost/nhost-js/auth";
// Re-export types that consumers might need
export type { NhostClient, Session };

/**
 * Cached client instances for server and client environments.
 * We maintain separate instances because they use different storage backends.
 */
let serverClient: NhostClient | null = null;
let browserClient: NhostClient | null = null;

/**
 * Get or create the Nhost client singleton.
 *
 * This uses lazy initialization to ensure:
 * - Server-side: Uses MemoryStorage (no localStorage available)
 * - Client-side: Uses LocalStorage for session persistence
 *
 * The lazy pattern is crucial for Next.js SSR because the module is first
 * imported on the server. Without lazy init, the server-created MemoryStorage
 * client would be reused on the client, losing localStorage session data.
 */
function getNhostClient(): NhostClient {
  const isBrowser = typeof window !== "undefined";

  if (isBrowser) {
    if (!browserClient) {
      browserClient = createClient({
        authUrl: env.NEXT_PUBLIC_NHOST_AUTH_URL,
        graphqlUrl: env.NEXT_PUBLIC_NHOST_GQL_URL,
        storageUrl: env.NEXT_PUBLIC_NHOST_STORAGE_URL,
        functionsUrl: env.NEXT_PUBLIC_NHOST_FUNCTIONS_URL,
        storage: new LocalStorage(),
      });
    }
    return browserClient;
  } else {
    if (!serverClient) {
      serverClient = createClient({
        authUrl: env.NEXT_PUBLIC_NHOST_AUTH_URL,
        graphqlUrl: env.NEXT_PUBLIC_NHOST_GQL_URL,
        storageUrl: env.NEXT_PUBLIC_NHOST_STORAGE_URL,
        functionsUrl: env.NEXT_PUBLIC_NHOST_FUNCTIONS_URL,
        storage: new MemoryStorage(),
      });
    }
    return serverClient;
  }
}

/**
 * Singleton Nhost client for usage throughout the app.
 *
 * This is a proxy that delegates to the appropriate client instance
 * based on the current environment (server vs browser).
 *
 * The client includes automatic session refresh middleware and
 * localStorage-based session persistence in the browser.
 */
export const nhost: NhostClient = new Proxy({} as NhostClient, {
  get(_target, prop) {
    const client = getNhostClient();
    const value = (client as any)[prop];
    // Bind methods to the client instance
    if (typeof value === "function") {
      return value.bind(client);
    }
    return value;
  },
});

/**
 * Create a plain Nhost client for server-side use (API routes, server actions).
 * No auth — pass `{ headers: adminHeaders() }` for admin requests.
 */
export function createServerNhostClient(): NhostClient {
  return createClient({
    authUrl: env.NEXT_PUBLIC_NHOST_AUTH_URL,
    graphqlUrl: env.NEXT_PUBLIC_NHOST_GQL_URL,
    storageUrl: env.NEXT_PUBLIC_NHOST_STORAGE_URL,
    functionsUrl: env.NEXT_PUBLIC_NHOST_FUNCTIONS_URL,
  });
}

/**
 * Admin secret header for Hasura GraphQL requests.
 * Works with both `nhostClient.graphql.request()` and Apollo `context.headers`.
 */
export function adminHeaders() {
  return {
    "x-hasura-admin-secret": requireServerEnv(env.NHOST_ADMIN_SECRET, "NHOST_ADMIN_SECRET"),
  };
}

/**
 * Create an admin client for server-side operations.
 *
 * IMPORTANT: Only use this on the server. Never expose admin secret to the client.
 */
export function createAdminClient(): NhostClient {
  const adminSecret = env.NHOST_ADMIN_SECRET;
  if (!adminSecret) {
    throw new Error("NHOST_ADMIN_SECRET is required for admin client");
  }

  return createNhostClient({
    authUrl: env.NEXT_PUBLIC_NHOST_AUTH_URL,
    graphqlUrl: env.NEXT_PUBLIC_NHOST_GQL_URL,
    storageUrl: env.NEXT_PUBLIC_NHOST_STORAGE_URL,
    functionsUrl: env.NEXT_PUBLIC_NHOST_FUNCTIONS_URL,
    configure: [
      withAdminSession({
        adminSecret,
      }),
    ],
  });
}

// ============================================================================
// GraphQL Helpers
// ============================================================================

/**
 * Unwrap GraphQL response from Nhost v4 SDK.
 * The v4 SDK returns data under `response.body.data` instead of `response.data`.
 * This utility handles both formats for compatibility.
 */
export function unwrapGraphQLResponse<T>(response: {
  body?: { data?: T };
  data?: T;
}): T | undefined {
  return response.body?.data ?? response.data;
}
